import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Field from "../Field";
import { Trophy } from "lucide-react";

interface CreateGameForm {
  name: string;
  date: string;
  formatId: number;
  tournamentId: number;
  isRated: boolean;
  tierId: number | null;
}

interface RatingTier {
  id: number;
  code: string;
  name: string;
  description: string;
  level: string;
  ageGroup: string;
  minTeams: number;
  weight: number;
}

interface TournamentOption {
  id: number;
  name: string;
  added?: string | null;
}

interface CreateOrganizerGameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  createGameForm: CreateGameForm;
  setCreateGameForm: React.Dispatch<React.SetStateAction<CreateGameForm>>;
  gameTournaments: TournamentOption[];
  gameFormats: any[];
  ratingTiers: RatingTier[];
  loading: boolean;
  creatingTournament: boolean;
  onCreateTournament: (name: string) => Promise<void> | void;
  onCreate: () => void;
}

const LEVEL_LABELS: Record<string, string> = {
  local:    "Yerli",
  national: "Ölkə",
};

const CreateOrganizerGameDialog: React.FC<CreateOrganizerGameDialogProps> = ({
  open,
  onOpenChange,
  createGameForm,
  setCreateGameForm,
  gameTournaments,
  gameFormats,
  ratingTiers,
  loading,
  creatingTournament,
  onCreateTournament,
  onCreate,
}) => {
  const [step, setStep] = React.useState<1 | 2 | 3>(1);
  const [tournamentQuery, setTournamentQuery] = React.useState("");
  const [tournamentOpen, setTournamentOpen] = React.useState(false);
  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      setStep(1);
      setTournamentOpen(false);
      const selected = gameTournaments.find((t) => t.id === createGameForm.tournamentId);
      setTournamentQuery(selected?.name ?? "");
    }
    if (!open) {
      setTournamentOpen(false);
    }
    wasOpenRef.current = open;
  }, [open, gameTournaments, createGameForm.tournamentId]);

  React.useEffect(() => {
    if (!open) return;
    const selected = gameTournaments.find((t) => t.id === createGameForm.tournamentId);
    if (selected) {
      setTournamentQuery(selected.name);
    }
  }, [open, gameTournaments, createGameForm.tournamentId]);

  const canGoStep2 =
    Boolean(createGameForm.name.trim()) &&
    Boolean(createGameForm.date) &&
    Boolean(createGameForm.tournamentId);

  const canGoStep3 = Boolean(createGameForm.formatId);

  const normalize = (v: string) =>
    v
      .toLocaleLowerCase("az")
      .replace(/[ə]/g, "e")
      .replace(/[ğ]/g, "g")
      .replace(/[ı]/g, "i")
      .replace(/[ö]/g, "o")
      .replace(/[ü]/g, "u")
      .replace(/[ş]/g, "s")
      .replace(/[ç]/g, "c")
      .replace(/\s+/g, " ")
      .trim();

  const normalizedQuery = normalize(tournamentQuery);
  const filteredTournaments = gameTournaments.filter((t) =>
    !normalizedQuery || normalize(t.name).includes(normalizedQuery)
  );
  const hasExactTournament = filteredTournaments.some((t) => normalize(t.name) === normalizedQuery);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Oyun yarat</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Addım {step}/3</p>

          {step === 1 && (
            <>
              <Field label="Oyunun adı">
                <Input
                  placeholder="Məs: Bakı Çempionatı 2026"
                  value={createGameForm.name}
                  onChange={(e) => setCreateGameForm((f) => ({ ...f, name: e.target.value }))}
                />
              </Field>
              <Field label="Tarix">
                <input
                  type="datetime-local"
                  value={createGameForm.date}
                  onChange={(e) => setCreateGameForm((f) => ({ ...f, date: e.target.value }))}
                  className="profile-datetime-input w-full h-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                />
              </Field>
              <Field label="Turnir">
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      placeholder="Turnir axtar..."
                      value={tournamentQuery}
                      onFocus={() => setTournamentOpen(true)}
                      onBlur={() => setTimeout(() => setTournamentOpen(false), 120)}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTournamentQuery(value);
                        setTournamentOpen(true);
                        setCreateGameForm((f) => ({ ...f, tournamentId: 0 }));
                      }}
                    />
                    {tournamentOpen && (
                      <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto">
                        {filteredTournaments.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setCreateGameForm((f) => ({ ...f, tournamentId: t.id }));
                              setTournamentQuery(t.name);
                              setTournamentOpen(false);
                            }}
                          >
                            <div className="font-medium text-gray-800 dark:text-gray-100">{t.name}</div>
                            <div className="text-[11px] text-gray-400 dark:text-gray-500">ID: {t.id}</div>
                          </button>
                        ))}
                        {filteredTournaments.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">Uyğun turnir tapılmadı</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {createGameForm.tournamentId
                        ? `Seçilən turnir ID: ${createGameForm.tournamentId}`
                        : "Turnir seçilməyib"}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={creatingTournament || !tournamentQuery.trim() || hasExactTournament}
                      onClick={() => onCreateTournament(tournamentQuery)}
                    >
                      {creatingTournament ? "Yaradılır..." : "Yeni turnir yarat"}
                    </Button>
                  </div>
                </div>
              </Field>
              <Button onClick={() => setStep(2)} disabled={!canGoStep2} className="w-full">
                Növbəti
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 block">Format *</label>
                <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto pr-0.5">
                  {gameFormats.map((f: any) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setCreateGameForm((form) => ({ ...form, formatId: f.id }))}
                      className={cn(
                        "flex flex-col items-start px-3 py-2 rounded-xl border-2 transition-all text-left",
                        createGameForm.formatId === f.id
                          ? "border-blue-500 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-indigo-300"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-300"
                      )}
                    >
                      <span className="text-xs font-semibold leading-snug">{f.name}</span>
                      <span className="text-[10px] opacity-60 mt-0.5 leading-tight truncate w-full block">
                        {Array.isArray(f.rounds)
                          ? `${f.rounds.length} tur · ${f.rounds.map((r: any) => r.questionCount).join("+")} s.`
                          : ""}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => setStep(1)} disabled={loading}>
                  Geri
                </Button>
                <Button onClick={() => setStep(3)} disabled={!canGoStep3 || loading}>
                  Növbəti
                </Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setCreateGameForm((f) => ({ ...f, isRated: !f.isRated, tierId: null }))}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all text-sm",
                    createGameForm.isRated
                      ? "border-amber-400 bg-amber-50 text-amber-700 font-semibold"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-500 dark:text-gray-400"
                  )}
                >
                  <Trophy className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 text-left">Reytinqli oyun</span>
                  <div className={cn(
                    "w-10 h-5 rounded-full transition-all flex items-center",
                    createGameForm.isRated ? "bg-amber-400 justify-end" : "bg-gray-200 justify-start"
                  )}>
                    <div className="w-4 h-4 bg-white dark:bg-gray-900 rounded-full mx-0.5 shadow-sm" />
                  </div>
                </button>

                {createGameForm.isRated && ratingTiers.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Kateqorİya seçin *</label>
                    <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto pr-1">
                      {Object.entries(
                        ratingTiers.reduce((acc: Record<string, RatingTier[]>, t) => {
                          (acc[t.level] = acc[t.level] || []).push(t);
                          return acc;
                        }, {})
                      ).map(([level, tiers]) => (
                        <div key={level}>
                          <div className="text-xs font-bold text-gray-400 dark:text-gray-500 px-1 py-0.5">{LEVEL_LABELS[level] || level}</div>
                          {tiers.map((tier) => (
                            <button
                              key={tier.id}
                              type="button"
                              onClick={() => setCreateGameForm((f) => ({ ...f, tierId: tier.id }))}
                              className={cn(
                                "w-full text-left px-3 py-2 rounded-lg border text-sm transition-all",
                                createGameForm.tierId === tier.id
                                  ? "border-amber-400 bg-amber-50 text-amber-800 font-semibold"
                                  : "border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-300"
                              )}
                            >
                              <div className="font-medium">{tier.name}</div>
                              <div className="text-xs opacity-60">{tier.description}</div>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => setStep(2)} disabled={loading}>
                  Geri
                </Button>
                <Button onClick={onCreate} disabled={loading}>
                  Göndər
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateOrganizerGameDialog;
